import type { Dashboard as DashboardPayload } from '@stockwait/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { EmptyState, ErrorState, LoadingState, MetricTile } from '../components/StateViews'
import { money, number, percent, shortDate } from '../lib/format'
import { useApi } from '../lib/useApi'

/**
 * The landing screen (SW-602). It must answer "is this app making me money?" in one glance.
 */
export function Dashboard() {
  const navigate = useNavigate()
  const [days, setDays] = useState(30)
  const { data, error, loading, reload } = useApi<DashboardPayload>(`/dashboard?days=${days}`)

  if (loading && !data) return <LoadingState label="Loading your dashboard" />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  const { headline, currency } = data

  if (data.isEmpty) {
    return (
      <EmptyState
        heading="Nothing waiting yet"
        action={{ label: 'Finish setup', onClick: () => navigate('/onboarding') }}
      >
        Once the button is live on your storefront, shoppers who hit a sold-out product can join the
        waitlist, and this screen fills with what that demand is worth. Setup takes about two
        minutes.
      </EmptyState>
    )
  }

  const peak = Math.max(1, ...data.series.map((point) => point.alertsSent))

  return (
    <s-stack direction="block" gap="large">
      <s-section>
        <s-stack direction="block" gap="base">
          <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
            <s-heading>Last {days} days</s-heading>
            <s-select
              label="Range"
              labelAccessibilityVisibility="exclusive"
              value={String(days)}
              onChange={(event: Event) =>
                setDays(Number((event.target as HTMLSelectElement).value))
              }
            >
              <s-option value="7">7 days</s-option>
              <s-option value="30">30 days</s-option>
              <s-option value="90">90 days</s-option>
            </s-select>
          </s-stack>

          <s-grid gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))" gap="base">
            <MetricTile
              label="Recovered revenue"
              value={money(headline.recoveredRevenueMinor, currency)}
              detail="Line-item value, 72h window"
            />
            <MetricTile label="Alerts sent" value={number(headline.alertsSent)} />
            <MetricTile
              label="Conversion rate"
              value={percent(headline.conversionRate)}
              detail={`${number(headline.conversions)} orders`}
            />
            <MetricTile label="Shoppers waiting" value={number(headline.waitingSubscribers)} />
          </s-grid>
        </s-stack>
      </s-section>

      {/*
        Waiting demand in dollars, given its own section rather than buried in the metric row.
        This is the Epic 8 hook: the merchant sees the number and the next question forms
        itself: "so what should I reorder?", which is exactly what the paid tier answers.
      */}
      <s-section heading="Demand waiting right now">
        <s-stack direction="block" gap="base">
          <s-stack direction="inline" gap="base" alignItems="baseline">
            <s-heading accessibilityRole="none">
              {money(headline.waitingDemandMinor, currency)}
            </s-heading>
            <s-text color="subdued">
              across {number(headline.waitingSubscribers)} shopper
              {headline.waitingSubscribers === 1 ? '' : 's'}
            </s-text>
          </s-stack>
          <s-paragraph color="subdued">
            Shoppers waiting × the price they saw. This is captured demand, not a forecast. How much
            of it converts depends on what you restock and when.
          </s-paragraph>
          <s-stack direction="inline">
            <s-button variant="primary" onClick={() => navigate('/recommendations')}>
              See what to restock
            </s-button>
          </s-stack>
        </s-stack>
      </s-section>

      <s-section heading="Most waited-on">
        {data.topProducts.length === 0 ? (
          <s-paragraph color="subdued">No waitlists yet.</s-paragraph>
        ) : (
          <s-table>
            <s-table-header-row>
              <s-table-header>Product</s-table-header>
              <s-table-header>Waiting</s-table-header>
              <s-table-header>Demand</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {data.topProducts.map((product) => (
                <s-table-row key={product.variantId}>
                  <s-table-cell>
                    <s-stack direction="inline" gap="small-300" alignItems="center">
                      {product.imageUrl ? (
                        <s-thumbnail src={product.imageUrl} alt="" size="small" />
                      ) : null}
                      <s-stack direction="block">
                        <s-text type="strong">{product.productTitle ?? 'Product'}</s-text>
                        {product.variantTitle && product.variantTitle !== 'Default Title' ? (
                          <s-text color="subdued">{product.variantTitle}</s-text>
                        ) : null}
                      </s-stack>
                    </s-stack>
                  </s-table-cell>
                  <s-table-cell>{number(product.waitingSubscribers)}</s-table-cell>
                  <s-table-cell>{money(product.demandValueMinor, currency)}</s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}
      </s-section>

      <s-section heading="Alerts over time">
        {/*
          A CSS-grid sparkline rather than a charting library. The whole series is at most 90
          points, and a 90 KB dependency to draw 90 bars inside an iframe that must load fast
          is a bad trade, App Bridge load-time is a Built for Shopify requirement (SW-1002).
        */}
        <s-stack direction="block" gap="small-300">
          <s-box padding="small-300">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${data.series.length}, 1fr)`,
                alignItems: 'end',
                gap: '2px',
                height: '96px',
              }}
            >
              {data.series.map((point) => (
                <div
                  key={point.day}
                  title={`${shortDate(point.day)}: ${point.alertsSent} alerts, ${money(point.recoveredRevenueMinor, currency)} recovered`}
                  style={{
                    height: `${Math.max(2, (point.alertsSent / peak) * 100)}%`,
                    background:
                      point.alertsSent > 0
                        ? 'var(--s-color-fill-brand, #303030)'
                        : 'var(--s-color-fill-subdued, #e3e3e3)',
                    borderRadius: '2px',
                  }}
                />
              ))}
            </div>
          </s-box>
          <s-stack direction="inline" justifyContent="space-between">
            <s-text color="subdued">{shortDate(data.range.from)}</s-text>
            <s-text color="subdued">{shortDate(data.range.to)}</s-text>
          </s-stack>
        </s-stack>
      </s-section>
    </s-stack>
  )
}
